import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    FullBleed,
    IconLocalShipping,
    Stack,
    Tabs,
    useConditionalBackOverride,
} from '@myphx-engineering/phx-design-system';
import { useIsMobile } from '@myphx-engineering/phx-design-system/hooks';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import type {
    AuthorizedMedicationInfoFragment,
    MailOrderProviderInfoFragment,
    PharmacyProviderInfoFragment,
    UnknownProviderInfoFragment,
} from '../../../../graphql/generated/graphql';
import { getPricing } from '../../../../util/config/get-pricing';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

import { ProviderOffersCardList } from './cards';

type ProviderSelectionProps = {
    header: React.ReactNode;
    providers: (
        | MailOrderProviderInfoFragment
        | PharmacyProviderInfoFragment
        | UnknownProviderInfoFragment
    )[];
    medication: AuthorizedMedicationInfoFragment;
    onSelect: (providerOffersId: string, providerId?: string) => void;
    drxId: string;
};

export const ProviderSelection = ({
    header,
    providers,
    medication,
    onSelect,
    drxId,
}: ProviderSelectionProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile('md');
    const navigate = useNavigate();

    const medConfig = useMedicationConfigOverride();
    const hasPricing = Boolean(getPricing(medConfig, medication));

    const backToMedCab = useCallback(() => {
        navigate(getAbsoluteRoute('cabinet.root'));
    }, [navigate]);

    useConditionalBackOverride(backToMedCab, !hasPricing);

    if (!providers) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    return (
        <Stack gap="lg">
            <Stack gap="sm">{header}</Stack>

            <Tabs
                defaultValue="delivery"
                grow
                tabBarProps={{ fullBleed: isMobile }}
                tabs={[
                    {
                        label: t('pickYourPharmacy.delivery'),
                        panel: (
                            <FullBleed breakpoint="md" color="primary">
                                <ProviderOffersCardList
                                    providers={providers}
                                    medication={medication}
                                    onSelect={onSelect}
                                    drxId={drxId}
                                />
                            </FullBleed>
                        ),
                        value: 'delivery',
                        LeftIcon: IconLocalShipping,
                    },
                ]}
            />
        </Stack>
    );
};
